// src/pages/sm/LatePolicyManager.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Fade } from '@mui/material';
import axios from 'axios';
import {
    Box, Typography, Button, Card, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, FormControlLabel, Switch, Grid, Stack, Tooltip,
    Chip, Select, MenuItem,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Gavel as GavelIcon,
    DeleteOutline as DeleteOutlineIcon,
} from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import AppHeader from '../../components/AppHeader';

const LatePolicyManager = ({ setIsLoggedIn }) => {
    const [policies, setPolicies] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingPolicy, setEditingPolicy] = useState(null);

    // Form State
    const [formData, setFormData] = useState({
        policy_name: '',
        description: '',
        is_active: 1,
        include_early_leave_in_strikes: 0,
        progressive_strikes: 0,
        tiers: [],
        strikes: []
    });

    const fetchPolicies = useCallback(async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/late-policies`, {
                withCredentials: true,
                headers: { 'X-Tenant': localStorage.getItem('tenant') }
            });
            if (res.data.success) {
                setPolicies(res.data.data);
            }
        } catch (err) {
            console.error("Error fetching policies:", err);
        }
    }, []);

    useEffect(() => {
        fetchPolicies();
    }, [fetchPolicies]);

    const handleOpenDialog = useCallback((policy = null) => {
        if (policy) {
            setEditingPolicy(policy);
            setFormData({
                policy_name: policy.policy_name,
                description: policy.description || '',
                is_active: policy.is_active,
                include_early_leave_in_strikes: policy.include_early_leave_in_strikes || 0,
                progressive_strikes: policy.progressive_strikes || 0,
                tiers: policy.tiers || [],
                strikes: policy.strikes || []
            });
        } else {
            setEditingPolicy(null);
            setFormData({
                policy_name: '',
                description: '',
                is_active: 1,
                include_early_leave_in_strikes: 0,
                progressive_strikes: 0,
                tiers: [],
                strikes: []
            });
        }
        setOpenDialog(true);
    }, []);

    const handleCloseDialog = useCallback(() => {
        setOpenDialog(false);
        setEditingPolicy(null);
    }, []);

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
        }));
    };

    const handleAddTier = () => {
        setFormData(prev => ({
            ...prev,
            tiers: [...prev.tiers, { min_minutes: 0, max_minutes: 60, deduction_type: 'FIXED_AMOUNT', deduction_value: 0, leave_type_code: null, tier_category: 'LATE' }]
        }));
    };

    const handleRemoveTier = (index) => {
        setFormData(prev => ({
            ...prev,
            tiers: prev.tiers.filter((_, i) => i !== index)
        }));
    };

    const handleTierChange = (index, field, value) => {
        const newTiers = [...formData.tiers];
        newTiers[index][field] = value;
        setFormData(prev => ({ ...prev, tiers: newTiers }));
    };

    const handleAddStrike = () => {
        setFormData(prev => ({
            ...prev,
            strikes: [...prev.strikes, { strike_threshold: 3, deduction_type: 'LEAVE_DEDUCTION', deduction_value: 1, leave_type_code: 'UNPAID', is_recurring: 1 }]
        }));
    };

    const handleRemoveStrike = (index) => {
        setFormData(prev => ({
            ...prev,
            strikes: prev.strikes.filter((_, i) => i !== index)
        }));
    };

    const handleStrikeChange = (index, field, value) => {
        const newStrikes = [...formData.strikes];
        newStrikes[index][field] = value;
        setFormData(prev => ({ ...prev, strikes: newStrikes }));
    };

    const handleSubmit = async () => {
        if (!formData.policy_name) {
            alert("Policy name is required");
            return;
        }
        try {
            const payload = {
                ...formData,
                id: editingPolicy?.id || null
            };
            const res = await axios.post(`${process.env.REACT_APP_API_URL1}/sm/late-policies`, payload, {
                withCredentials: true,
                headers: { 'X-Tenant': localStorage.getItem('tenant') }
            });

            if (res.data.success) {
                fetchPolicies();
                handleCloseDialog();
            }
        } catch (err) {
            console.error("Error saving policy:", err);
            alert(err.response?.data?.message || "Error saving policy");
        }
    };

    const handleDelete = useCallback(async (id) => {
        if (!window.confirm("Are you sure you want to delete this policy?")) return;
        try {
            const res = await axios.delete(`${process.env.REACT_APP_API_URL1}/sm/late-policies/${id}`, {
                withCredentials: true,
                headers: { 'X-Tenant': localStorage.getItem('tenant') }
            });
            if (res.data.success) {
                fetchPolicies();
            }
        } catch (err) {
            console.error("Error deleting policy:", err);
            alert(err.response?.data?.message || "Error deleting policy");
        }
    }, [fetchPolicies]);

    const columnDefs = useMemo(() => [
        {
            headerName: "Policy Name",
            field: "policy_name",
            flex: 1,
            cellStyle: { fontWeight: 800, color: '#1e293b' }
        },
        {
            headerName: "Rules",
            width: 250,
            cellRenderer: params => {
                const tierCount = params.data.tiers?.length || 0;
                const strikeCount = params.data.strikes?.length || 0;
                return (
                    <Stack direction="row" spacing={0.5}>
                        <Chip label={`${tierCount} Tiers`} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
                        <Chip label={`${strikeCount} Strikes`} size="small" variant="outlined" color="primary" sx={{ fontWeight: 600 }} />
                    </Stack>
                );
            }
        },
        {
            headerName: "Status",
            field: "is_active",
            width: 110,
            cellRenderer: params => (
                <Chip
                    label={params.value === 1 ? "Active" : "Inactive"}
                    size="small"
                    sx={{ 
                        fontWeight: 800, fontSize: 10,
                        bgcolor: params.value === 1 ? '#ecfdf5' : '#f1f5f9',
                        color: params.value === 1 ? '#10b981' : '#64748b',
                        border: '1px solid',
                        borderColor: params.value === 1 ? '#d1fae5' : '#e2e8f0'
                    }}
                />
            )
        },
        {
            headerName: "Actions",
            width: 120,
            pinned: 'right',
            cellRenderer: params => (
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                    <Tooltip title="Edit Policy">
                        <IconButton size="small" onClick={() => handleOpenDialog(params.data)} sx={{ color: 'primary.main', '&:hover': { bgcolor: '#f5f3ff' } }}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(params.data.id)} sx={{ color: '#ef4444', '&:hover': { bgcolor: '#fef2f2' } }}>
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            )
        }
    ], [handleOpenDialog, handleDelete]);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <AppHeader setIsLoggedIn={setIsLoggedIn} />

            <Box sx={{ p: 4, maxWidth: "1600px", mx: "auto" }}>
                <Fade in timeout={500}>
                    <Card sx={{ p: 4, borderRadius: 4, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.03)', border: '1px solid #f1f5f9' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                            <Box>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Box sx={{ p: 1, bgcolor: '#f1f5f9', borderRadius: 2, display: 'flex' }}>
                                        <GavelIcon sx={{ color: 'primary.main' }} />
                                    </Box>
                                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
                                        Attendance Compliance
                                    </Typography>
                                </Stack>
                                <Typography variant="body2" sx={{ color: '#64748b', mt: 1, ml: 6 }}>
                                    Define rules for automated lateness deductions, early leave penalties, and strike system thresholds.
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenDialog()}
                                sx={{
                                    bgcolor: 'primary.main', borderRadius: 2.5, px: 3, fontWeight: 700, textTransform: 'none',
                                    boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}33`, '&:hover': { bgcolor: 'primary.dark' }
                                }}
                            >
                                Create New Policy
                            </Button>
                        </Box>

                        <Box className="ag-theme-quartz" sx={{ height: 620, width: '100%', '& .ag-header': { bgcolor: '#f8fafc' } }}>
                            <AgGridReact
                                rowData={policies}
                                columnDefs={columnDefs}
                                defaultColDef={{
                                    sortable: true,
                                    filter: true,
                                    resizable: true
                                }}
                                rowHeight={56}
                                headerHeight={52}
                                pagination={true}
                                paginationPageSize={10}
                                animateRows={true}
                            />
                        </Box>
                    </Card>
                </Fade>
            </Box>

            {/* POLICY DIALOG */}
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 4, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' } }}
            >
                <DialogTitle sx={{ p: 3, pb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ p: 1, bgcolor: '#f5f3ff', borderRadius: 2, display: 'flex' }}>
                        <GavelIcon sx={{ color: 'primary.main' }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                            {editingPolicy ? "Update Compliance Policy" : "New Compliance Policy"}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>Manage automated penalty structures and thresholds.</Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 3, pt: 1 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: '#475569' }}>Policy Name</Typography>
                            <TextField fullWidth name="policy_name" placeholder="e.g. Standard Corporate Policy" value={formData.policy_name} onChange={handleChange} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: '#f8fafc' } }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: '#475569' }}>Operation Mode</Typography>
                            <Box sx={{ p: 1.5, borderRadius: 2.5, border: '1px solid #e2e8f0', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center' }}>
                                <FormControlLabel
                                    control={<Switch checked={formData.is_active === 1} onChange={handleChange} name="is_active" color="success" />}
                                    label={<Typography variant="body2" sx={{ fontWeight: 700, color: formData.is_active ? '#059669' : '#64748b' }}>{formData.is_active ? 'ENABLED' : 'DISABLED'}</Typography>}
                                    sx={{ m: 0 }}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: '#475569' }}>Internal Description</Typography>
                            <TextField fullWidth multiline rows={2} name="description" placeholder="Briefly describe the implementation of this policy..." value={formData.description} onChange={handleChange} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: '#f8fafc' } }} />
                        </Grid>
                        <Grid item xs={12}>
                            <Paper sx={{ p: 2, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                                <Stack direction="row" spacing={4}>
                                    <FormControlLabel
                                        control={<Switch checked={formData.include_early_leave_in_strikes === 1} onChange={handleChange} name="include_early_leave_in_strikes" sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: 'primary.main' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: 'primary.main' } }} />}
                                        label={<Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>Enforce Early Leave Penalties</Typography>}
                                    />
                                    <FormControlLabel
                                        control={<Switch checked={formData.progressive_strikes === 1} onChange={handleChange} name="progressive_strikes" sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#f59e0b' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#f59e0b' } }} />}
                                        label={<Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>Progressive Strike Enforcement</Typography>}
                                    />
                                </Stack>
                            </Paper>
                        </Grid>
                        
                        {/* TIERS SECTION */}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, mb: 2 }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1e293b' }}>Lateness & Early Leave Tiers</Typography>
                                    <Chip label="FIXED" size="small" sx={{ fontWeight: 800, fontSize: 10, bgcolor: '#eff6ff', color: '#3b82f6', height: 20 }} />
                                </Stack>
                                <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={handleAddTier} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, color: 'primary.main', borderColor: 'primary.main' }}>Add Penalty Tier</Button>
                            </Box>
                            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                                <Table size="small">
                                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.5 }}>Category</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Range (Mins)</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Deduction Policy</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Value</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Leave Map</TableCell>
                                            <TableCell align="center" sx={{ width: 60 }}></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {formData.tiers.map((tier, idx) => (
                                            <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#fcfcfc' } }}>
                                                <TableCell>
                                                    <Select size="small" value={tier.tier_category || 'LATE'} onChange={(e) => handleTierChange(idx, 'tier_category', e.target.value)} sx={{ borderRadius: 2, bgcolor: '#fff', fontSize: '0.875rem' }}>
                                                        <MenuItem value="LATE">Lateness</MenuItem>
                                                        <MenuItem value="EARLY_LEAVE">Early Leave</MenuItem>
                                                    </Select>
                                                </TableCell>
                                                <TableCell>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <TextField size="small" type="number" value={tier.min_minutes} onChange={(e) => handleTierChange(idx, 'min_minutes', parseInt(e.target.value))} sx={{ width: 80, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                                                        <Typography variant="caption">—</Typography>
                                                        <TextField size="small" type="number" value={tier.max_minutes} onChange={(e) => handleTierChange(idx, 'max_minutes', parseInt(e.target.value))} sx={{ width: 80, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    <Select size="small" value={tier.deduction_type} onChange={(e) => handleTierChange(idx, 'deduction_type', e.target.value)} sx={{ borderRadius: 2, bgcolor: '#fff', minWidth: 160 }}>
                                                        <MenuItem value="FIXED_AMOUNT">Fixed Value</MenuItem>
                                                        <MenuItem value="LEAVE_DEDUCTION">Leave Units</MenuItem>
                                                        <MenuItem value="BASIC_SALARY_PRO_RATA">Basic (Pro-rata)</MenuItem>
                                                        <MenuItem value="GROSS_SALARY_PRO_RATA">Gross (Pro-rata)</MenuItem>
                                                    </Select>
                                                </TableCell>
                                                <TableCell>
                                                    <TextField size="small" type="number" value={tier.deduction_value} onChange={(e) => handleTierChange(idx, 'deduction_value', parseFloat(e.target.value))} sx={{ width: 100, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField size="small" placeholder="UNPAID" value={tier.leave_type_code || ''} onChange={(e) => handleTierChange(idx, 'leave_type_code', e.target.value)} disabled={tier.deduction_type !== 'LEAVE_DEDUCTION'} sx={{ width: 100, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton size="small" sx={{ color: '#ef4444' }} onClick={() => handleRemoveTier(idx)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>

                        {/* STRIKES SECTION */}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, mb: 2 }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1e293b' }}>Recurring Strike System</Typography>
                                    <Chip label="RECURRING" size="small" sx={{ fontWeight: 800, fontSize: 10, bgcolor: '#fdf2f8', color: '#db2777', height: 20 }} />
                                </Stack>
                                <Button startIcon={<AddIcon />} variant="outlined" size="small" color="secondary" onClick={handleAddStrike} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, color: '#db2777', borderColor: '#db2777' }}>Configure New Strike</Button>
                            </Box>
                            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                                <Table size="small">
                                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.5 }}>Threshold (Occurrences)</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Auto-Recurring</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Deduction Mode</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Value</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Leave Map</TableCell>
                                            <TableCell align="center" sx={{ width: 60 }}></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {formData.strikes.map((strike, idx) => (
                                            <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#fcfcfc' } }}>
                                                <TableCell>
                                                    <TextField size="small" type="number" value={strike.strike_threshold} onChange={(e) => handleStrikeChange(idx, 'strike_threshold', parseInt(e.target.value))} placeholder="e.g. 3" sx={{ width: 100, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                                                </TableCell>
                                                <TableCell>
                                                    <FormControlLabel control={<Switch size="small" checked={strike.is_recurring === 1} onChange={(e) => handleStrikeChange(idx, 'is_recurring', e.target.checked ? 1 : 0)} color="secondary" />} label={<Typography variant="caption" sx={{ fontWeight: 700 }}>{strike.is_recurring ? 'YES' : 'NO'}</Typography>} />
                                                </TableCell>
                                                <TableCell>
                                                    <Select size="small" value={strike.deduction_type} onChange={(e) => handleStrikeChange(idx, 'deduction_type', e.target.value)} sx={{ borderRadius: 2, bgcolor: '#fff', minWidth: 160 }}>
                                                        <MenuItem value="FIXED_AMOUNT">Fixed Value</MenuItem>
                                                        <MenuItem value="LEAVE_DEDUCTION">Leave Units</MenuItem>
                                                        <MenuItem value="BASIC_SALARY_PRO_RATA">Basic (Pro-rata)</MenuItem>
                                                        <MenuItem value="GROSS_SALARY_PRO_RATA">Gross (Pro-rata)</MenuItem>
                                                    </Select>
                                                </TableCell>
                                                <TableCell>
                                                    <TextField size="small" type="number" value={strike.deduction_value} onChange={(e) => handleStrikeChange(idx, 'deduction_value', parseFloat(e.target.value))} sx={{ width: 100, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField size="small" placeholder="UNPAID" value={strike.leave_type_code || ''} onChange={(e) => handleStrikeChange(idx, 'leave_type_code', e.target.value)} disabled={strike.deduction_type !== 'LEAVE_DEDUCTION'} sx={{ width: 100, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton size="small" sx={{ color: '#ef4444' }} onClick={() => handleRemoveStrike(idx)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #f1f5f9' }}>
                    <Button onClick={handleCloseDialog} sx={{ fontWeight: 700, color: '#64748b', textTransform: 'none' }}>Discard Changes</Button>
                    <Button variant="contained" onClick={handleSubmit} sx={{ bgcolor: 'primary.main', px: 4, borderRadius: 2.5, fontWeight: 700, textTransform: 'none', boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}33`, '&:hover': { bgcolor: 'primary.dark' } }}>
                        {editingPolicy ? "Update Policy" : "Activate Policy"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LatePolicyManager;
